/**
 * The single gate for everything this site puts on a visitor's device.
 *
 * Two promises from the cookie banner and the privacy policy are enforced
 * here rather than at each call site: nothing is written before the visitor
 * has accepted the matching category, and withdrawing a category removes what
 * earlier visits left behind. Anything that wants to persist something goes
 * through writeStoredItem, and anything a category can leave behind is listed
 * below so that clearCategory can find it again.
 */
import { deleteCookie, getCookieNames } from './cookieUtils';

export type ConsentDecision = 'accepted' | 'declined' | null;

export interface CookiePreferences {
  preferences: boolean; // Language choice
  theme: boolean; // Theme mode (dark/light)
  analytics: boolean; // Google Analytics
}

export type ConsentCategory = keyof CookiePreferences;

export const NO_PREFERENCES: CookiePreferences = {
  preferences: false,
  theme: false,
  analytics: false,
};

export const ALL_PREFERENCES: CookiePreferences = {
  preferences: true,
  theme: true,
  analytics: true,
};

const CATEGORIES: ConsentCategory[] = ['preferences', 'theme', 'analytics'];

// The consent decision itself. Only ever written after an explicit "accept",
// so it needs no category of its own.
const CONSENT_KEYS = ['cookieConsent', 'cookiePreferences', 'cookieConsentData'];

const LOCAL_STORAGE_KEYS: Record<ConsentCategory, string[]> = {
  preferences: ['language'],
  theme: ['themeMode'],
  analytics: [],
};

const COOKIE_NAMES: Record<ConsentCategory, string[]> = {
  preferences: [],
  theme: ['themeMode'],
  analytics: [], // Google names its cookies after the measurement ID, see below
};

// _ga_G-XXXXXXX carries the measurement ID, so Google's cookies are matched by
// prefix instead of being listed by name.
const ANALYTICS_COOKIE_PREFIXES = ['_ga', '_gid', '_gat'];

/**
 * Storage can throw - Safari's private mode and a blocked-cookies setting both
 * do - and a visitor who cannot store anything is not a reason to break a page.
 */
export const readStoredItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const removeStoredItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    // Nothing was stored in the first place.
  }
};

export const readConsentDecision = (): ConsentDecision => {
  const stored = readStoredItem('cookieConsent');
  return stored === 'accepted' || stored === 'declined' ? stored : null;
};

export const readCookiePreferences = (): CookiePreferences => {
  if (readConsentDecision() !== 'accepted') {
    return NO_PREFERENCES;
  }
  const stored = readStoredItem('cookiePreferences');
  if (!stored) {
    return NO_PREFERENCES;
  }
  try {
    const parsed = JSON.parse(stored);
    return {
      preferences: parsed.preferences === true,
      theme: parsed.theme === true,
      analytics: parsed.analytics === true,
    };
  } catch (error) {
    // A preference we cannot read is not a preference we may act on.
    return NO_PREFERENCES;
  }
};

/** Whether the visitor has agreed to us storing this category on their device. */
export const isStorageAllowed = (category: ConsentCategory): boolean =>
  readCookiePreferences()[category];

/** Writes to localStorage only if the matching category was consented to. */
export const writeStoredItem = (category: ConsentCategory, key: string, value: string): void => {
  if (!isStorageAllowed(category)) {
    return;
  }
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    // Storage unavailable - the setting simply does not survive this visit.
  }
};

/** Removes everything a category may have left behind, storage and cookies alike. */
export const clearCategory = (category: ConsentCategory): void => {
  LOCAL_STORAGE_KEYS[category].forEach(removeStoredItem);
  COOKIE_NAMES[category].forEach(deleteCookie);

  if (category === 'analytics') {
    getCookieNames()
      .filter((name) => ANALYTICS_COOKIE_PREFIXES.some((prefix) => name.startsWith(prefix)))
      .forEach(deleteCookie);
  }
};

/** Drops every category the visitor did not (or no longer does) agree to. */
export const clearWithdrawnCategories = (preferences: CookiePreferences): void => {
  CATEGORIES.filter((category) => !preferences[category]).forEach(clearCategory);
};

/** Declining has to leave the device as clean as a first visit does. */
export const clearAllStorage = (): void => {
  CATEGORIES.forEach(clearCategory);
  CONSENT_KEYS.forEach(removeStoredItem);
};

export const persistConsent = (preferences: CookiePreferences): void => {
  try {
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    // Kept as evidence of consent under Art. 7 (1) GDPR.
    localStorage.setItem(
      'cookieConsentData',
      JSON.stringify({
        timestamp: new Date().toISOString(),
        consent: 'accepted',
        preferences,
      })
    );
  } catch (error) {
    // Without storage the banner reappears next visit, which is the safe end.
  }
};

/**
 * Consent lives in localStorage rather than in React state, so contexts that
 * persist something need a nudge when it changes. The provider publishes,
 * everyone who stores something subscribes.
 */
const CONSENT_CHANGED_EVENT = 'cookie-consent-changed';

export const publishConsentChange = (): void => {
  window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT));
};

export const subscribeToConsentChange = (handler: () => void): (() => void) => {
  window.addEventListener(CONSENT_CHANGED_EVENT, handler);
  return () => window.removeEventListener(CONSENT_CHANGED_EVENT, handler);
};
